import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import { CTABand } from "../components/CTABand";
import { Reveal } from "../components/Reveal";
import { IndustryIcon, ServiceIcon, IconCheck, IconArrow } from "../components/Icons";
import { getIndustry, getServices, mediaUrl } from "../lib/api";
import NotFound from "./NotFound";

export default function IndustryDetail() {
  const { slug } = useParams();
  const [ind, setInd] = useState(null);
  const [services, setServices] = useState([]);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true); setError(false);
    getIndustry(slug).then(setInd).catch(() => setError(true)).finally(() => setLoading(false));
    getServices().then(setServices).catch(() => {});
  }, [slug]);

  if (error) return <NotFound />;
  if (loading || !ind) return <div style={{ minHeight: "60vh" }} />;

  const svcMap = Object.fromEntries(services.map((s) => [s.slug, s]));
  const related = (ind.services || []).map((s) => svcMap[s]).filter(Boolean);

  return (
    <div data-testid="industry-detail-page">
      <PageHeader eyebrow={`Industry ${ind.number}`} title={ind.title} lead={ind.hero_lead} crumb={ind.title}
        image={mediaUrl(ind.image_media_id)} imageAlt={ind.image_alt} />

      <section className="detail-section bg-paper" data-testid="industry-overview">
        <div className="wrap">
          <Reveal className="d-lead">
            <div className="ic" style={{ width: 56, height: 56, borderRadius: 14, background: "var(--mist-2)", display: "grid", placeItems: "center", marginBottom: 22 }}>
              <IndustryIcon name={ind.icon} />
            </div>
            <span className="eyebrow">Who we serve</span>
            <h2>{ind.summary}</h2>
          </Reveal>
          <Reveal className="pill-list" style={{ marginTop: 24 }}>
            {ind.clients?.map((c) => <span key={c} data-testid="industry-client">{c}</span>)}
          </Reveal>
          {(ind.image_media_id || ind.secondary_media_id) && (
            <div className="photo-strip" style={{ marginTop: 36 }} data-testid="industry-photos">
              {[[ind.image_media_id, ind.image_alt], [ind.secondary_media_id, ind.secondary_alt]]
                .filter(([id]) => id)
                .map(([id, alt], i) => (
                  <Reveal key={id} delay={i * 60}>
                    <figure className="photo-card"><img src={mediaUrl(id)} alt={alt || ""} loading="lazy" /></figure>
                  </Reveal>
                ))}
            </div>
          )}
        </div>
      </section>

      <section className="detail-section bg-mist" data-testid="industry-challenges">
        <div className="wrap">
          <Reveal className="d-lead"><span className="eyebrow">Sector challenges</span><h2>What organizations in this sector face</h2></Reveal>
          <ul className="chk-grid" style={{ marginTop: 28 }}>
            {ind.challenges?.map((c) => <li key={c} data-testid="industry-challenge"><IconCheck /> {c}</li>)}
          </ul>
        </div>
      </section>

      <section className="detail-section bg-paper" data-testid="industry-support">
        <div className="wrap">
          <Reveal className="d-lead"><span className="eyebrow">How we help</span><h2>How Lemur Quality supports this sector</h2></Reveal>
          <div className="info-grid-2" style={{ marginTop: 30 }}>
            {ind.support?.map((s, i) => (
              <Reveal key={i} className="mini-card" delay={(i % 2) * 60} data-testid="industry-support-item">
                <h3>{s.title}</h3>
                <p>{s.description}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="detail-section bg-mist" data-testid="industry-services">
          <div className="wrap">
            <Reveal className="d-lead"><span className="eyebrow">Relevant services</span><h2>Services we bring to this sector</h2></Reveal>
            <div className="hub-grid three" style={{ marginTop: 30 }}>
              {related.map((r, i) => (
                <Reveal key={r.id} delay={(i % 3) * 60}>
                  <Link to={`/services/${r.slug}`} className="hub-card" data-testid={`industry-service-${r.slug}`}>
                    <div className="hc-ic"><ServiceIcon name={r.icon} /></div>
                    <h3>{r.title}</h3>
                    <p>{r.summary}</p>
                    <span className="more">Learn more <IconArrow /></span>
                  </Link>
                </Reveal>
              ))}
            </div>
            <div style={{ marginTop: 28 }}>
              <Link to="/industries" className="btn btn-ghost" data-testid="all-industries-link">All industries</Link>
            </div>
          </div>
        </section>
      )}

      <CTABand />
    </div>
  );
}
